import { initAdmin, restoreAdminSession } from './admin';
import { api } from './api';
import { loadPayload, refreshAnnotated, refreshPendingCount, schedulePayload } from './data';
import { $ } from './dom';
import { gotoFrameIndex, gotoFrameMs, initMarkers, setFrameByTime, stepSnapshot } from './frames';
import { initOverlay, handleDrawKey } from './overlay';
import { initPanel } from './panel';
import { initPendingDialog } from './pending';
import { initPlayer, stepFrame, togglePlay } from './player';
import { state } from './state';
import { initSuggestDialog } from './suggest';
import { initTermsUi } from './termsUi';
import { toast } from './toast';

async function boot(): Promise<void> {
  const [config, manifest] = await Promise.all([api.config(), api.manifest()]);
  state.config = config;
  state.manifest = manifest;
  if (manifest.frames.length === 0) {
    toast('No frames in manifest — run the build script.', 'err');
    return;
  }

  await restoreAdminSession(); // before the first payload load so admins get pending data

  initPlayer(setFrameByTime);
  initMarkers();
  initOverlay();
  initPanel();
  initSuggestDialog();
  initTermsUi();
  initPendingDialog();
  initAdmin();
  initDialogCloseButtons();
  initTransport();
  initKeyboard();

  state.addEventListener('frame', () => {
    // drop the previous frame's polygons right away — never draw them on the
    // wrong snapshot while the new payload loads
    state.payload = null;
    state.emit('payload');
    schedulePayload();
  });
  state.addEventListener('admin', () => {
    void loadPayload();
    void refreshPendingCount();
  });

  void refreshAnnotated();
  void refreshPendingCount();
  setFrameByTime(0);
  void loadPayload();

  // debug/test hook (used by scripts/smoke.mjs)
  (window as unknown as { __daicon?: unknown }).__daicon = { state, gotoFrameMs, stepFrame };
}

function initDialogCloseButtons(): void {
  document.querySelectorAll<HTMLElement>('[data-close]').forEach((btn) => {
    btn.addEventListener('click', () => btn.closest('dialog')?.close());
  });
}

function initTransport(): void {
  $('#btnStepBack').addEventListener('click', () => stepFrame(-1));
  $('#btnStepFwd').addEventListener('click', () => stepFrame(1));
  $('#btnPrevSnap').addEventListener('click', () => stepSnapshot(-1));
  $('#btnNextSnap').addEventListener('click', () => stepSnapshot(1));
  $('#btnSeekToFrame').addEventListener('click', () => gotoFrameIndex(state.frameIndex));
}

function initKeyboard(): void {
  document.addEventListener('keydown', (e) => {
    if (document.querySelector('dialog[open]')) return; // dialogs own the keyboard
    const target = e.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable]')) return;
    if (handleDrawKey(e)) {
      e.preventDefault();
      return;
    }
    switch (e.key) {
      case ' ':
        e.preventDefault();
        togglePlay();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (e.shiftKey) stepSnapshot(-1);
        else stepFrame(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (e.shiftKey) stepSnapshot(1);
        else stepFrame(1);
        break;
      case ',':
        stepFrame(-1);
        break;
      case '.':
        stepFrame(1);
        break;
    }
  });
}

boot().catch((err: unknown) => {
  console.error(err);
  toast('Failed to start the app — see console.', 'err');
});
